import React from "react";
import {
  FaAssistiveListeningSystems,
  FaCheck,
  FaDesktop,
  FaEnvelope,
  FaEye,
  FaKeyboard,
  FaUniversalAccess
} from "react-icons/fa";

import "../assets/css/accessibility.css";

const accessibilityFeatures = [
  {
    icon: FaKeyboard,
    title: "Keyboard Navigation",
    description:
      "We work to ensure that interactive website elements can be reached and operated using a keyboard."
  },
  {
    icon: FaEye,
    title: "Visual Accessibility",
    description:
      "We consider readable typography, color contrast, visible focus indicators, and scalable content."
  },
  {
    icon: FaAssistiveListeningSystems,
    title: "Assistive Technology",
    description:
      "We aim to use semantic structure and descriptive labels that support screen readers and other assistive technologies."
  },
  {
    icon: FaDesktop,
    title: "Responsive Experience",
    description:
      "Website content is designed to remain functional across supported desktop, tablet, and mobile devices."
  }
];

const accessibilityPractices = [
  "Use clear and consistent page structures",
  "Provide descriptive page titles and headings",
  "Add meaningful alternative text to informative images",
  "Maintain visible keyboard focus indicators",
  "Support text resizing and responsive layouts",
  "Use descriptive links, labels, and form instructions",
  "Avoid unnecessary flashing or rapidly moving content",
  "Review accessibility when website content is updated"
];

const Accessibility = () => {
  return (
    <main className="accessibility-page">
      {/* HERO */}
      <section
        className="accessibility-hero"
        aria-labelledby="accessibility-page-heading"
      >
        <div className="accessibility-hero__container">
          <div className="accessibility-hero__content">
            <span className="accessibility-hero__label">
              Inclusion &amp; Digital Access
            </span>

            <h1 id="accessibility-page-heading">Accessibility</h1>

            <p>
              Bethel Innovations is committed to providing a digital
              experience that is accessible and usable for the broadest
              possible audience.
            </p>

            <div className="accessibility-hero__meta">
              <span>Last reviewed August 12, 2026</span>
              <span aria-hidden="true" />
              <span>Bethel Innovations, LLC</span>
            </div>
          </div>
        </div>

        <div className="accessibility-hero__accent" aria-hidden="true" />
      </section>

      {/* COMMITMENT */}
      <section
        className="accessibility-commitment"
        aria-labelledby="accessibility-commitment-heading"
      >
        <div className="accessibility-container accessibility-commitment__layout">
          <div className="accessibility-commitment__heading">
            <span className="accessibility-eyebrow">Our Commitment</span>

            <h2 id="accessibility-commitment-heading">
              Digital access should support every visitor.
            </h2>
          </div>

          <div className="accessibility-commitment__copy">
            <p>
              Bethel Innovations recognizes that accessibility is an important
              part of delivering responsible and inclusive digital services.
              We seek to provide website content that can be accessed,
              understood, and used by people with a wide range of abilities.
            </p>

            <p>
              Accessibility is an ongoing responsibility. As our website,
              content, and technology evolve, we intend to continue evaluating
              opportunities to improve the user experience and address
              accessibility barriers.
            </p>
          </div>
        </div>
      </section>

      {/* ACCESSIBILITY GOAL */}
      <section className="accessibility-standard">
        <div className="accessibility-container">
          <div className="accessibility-standard__panel">
            <div className="accessibility-standard__icon">
              <FaUniversalAccess aria-hidden="true" />
            </div>

            <div className="accessibility-standard__content">
              <span>Accessibility Goal</span>

              <h2>Working toward recognized accessibility practices.</h2>

              <p>
                Our website is designed with reference to the Web Content
                Accessibility Guidelines, including WCAG 2.2 Level AA
                principles, where reasonably applicable. These guidelines
                organize accessibility around four core principles: content
                should be perceivable, operable, understandable, and robust.
              </p>

              <p className="accessibility-standard__note">
                This statement describes our accessibility objectives and
                ongoing efforts. It is not a formal certification or
                declaration that every page currently conforms to every WCAG
                success criterion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ACCESSIBILITY FEATURES */}
      <section
        className="accessibility-features"
        aria-labelledby="accessibility-features-heading"
      >
        <div className="accessibility-container">
          <header className="accessibility-section-header">
            <span className="accessibility-eyebrow">Website Experience</span>

            <h2 id="accessibility-features-heading">
              Accessibility considerations built into our approach.
            </h2>

            <p>
              We consider multiple ways that visitors navigate, read,
              understand, and interact with website content.
            </p>
          </header>

          <div className="accessibility-features__grid">
            {accessibilityFeatures.map((feature) => {
              const Icon = feature.icon;

              return (
                <article
                  className="accessibility-feature-card"
                  key={feature.title}
                >
                  <div className="accessibility-feature-card__icon">
                    <Icon aria-hidden="true" />
                  </div>

                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRACTICES */}
      <section
        className="accessibility-practices"
        aria-labelledby="accessibility-practices-heading"
      >
        <div className="accessibility-container accessibility-practices__layout">
          <div className="accessibility-practices__introduction">
            <span className="accessibility-eyebrow">Our Practices</span>

            <h2 id="accessibility-practices-heading">
              How we support an accessible website.
            </h2>

            <p>
              Our accessibility efforts may include design, development,
              content, testing, and maintenance practices intended to improve
              usability for visitors with disabilities.
            </p>
          </div>

          <div
            className="accessibility-practices__list"
            aria-label="Accessibility practices"
          >
            {accessibilityPractices.map((practice) => (
              <div
                className="accessibility-practices__item"
                key={practice}
              >
                <span>
                  <FaCheck aria-hidden="true" />
                </span>

                <p>{practice}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIMITATIONS */}
      <section
        className="accessibility-limitations"
        aria-labelledby="accessibility-limitations-heading"
      >
        <div className="accessibility-container accessibility-limitations__layout">
          <div>
            <span className="accessibility-eyebrow accessibility-eyebrow--light">
              Ongoing Improvement
            </span>

            <h2 id="accessibility-limitations-heading">
              We welcome opportunities to improve.
            </h2>
          </div>

          <div className="accessibility-limitations__copy">
            <p>
              Despite our efforts, some content or functionality may not yet
              provide an optimal experience for every visitor or assistive
              technology.
            </p>

            <p>
              Certain third-party websites, embedded services, documents, or
              platforms linked from this website are controlled by their
              respective providers. Bethel Innovations cannot control the
              accessibility of third-party content, but we welcome reports of
              barriers involving resources linked from our website.
            </p>
          </div>
        </div>
      </section>

      {/* FEEDBACK */}
      <section
        className="accessibility-feedback"
        aria-labelledby="accessibility-feedback-heading"
      >
        <div className="accessibility-container">
          <div className="accessibility-feedback__layout">
            <div className="accessibility-feedback__content">
              <span className="accessibility-eyebrow">
                Accessibility Assistance
              </span>

              <h2 id="accessibility-feedback-heading">
                Report an accessibility barrier.
              </h2>

              <p>
                If you experience difficulty accessing information or using a
                feature on this website, please contact us. We will make
                reasonable efforts to provide the information in an accessible
                format and evaluate the reported issue.
              </p>

              <p>
                To help us respond effectively, please identify the page or
                feature involved, describe the difficulty you experienced, and
                indicate your preferred method of contact.
              </p>
            </div>

            <address className="accessibility-contact-card">
              <div className="accessibility-contact-card__icon">
                <FaEnvelope aria-hidden="true" />
              </div>

              <div>
                <span>Accessibility Contact</span>
                <strong>Bethel Innovations, LLC</strong>

                <a
                  href="mailto:info@bethelinnovations.com?subject=Website%20Accessibility%20Request"
                >
                  info@bethelinnovations.com
                </a>

                <a href="tel:+17034550650">(703) 455-0650</a>
              </div>
            </address>
          </div>

          <div className="accessibility-feedback__response">
            <span>When contacting us, please include:</span>

            <ul>
              <li>The page address or name of the affected content.</li>
              <li>A brief description of the accessibility barrier.</li>
              <li>
                The browser, device, or assistive technology used, if
                applicable.
              </li>
              <li>Your preferred method for receiving a response.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* POLICY INFORMATION */}
      <section className="accessibility-document-note">
        <div className="accessibility-container">
          <div className="accessibility-document-note__layout">
            <FaUniversalAccess aria-hidden="true" />

            <div>
              <h2>Accessibility Statement Updates</h2>

              <p>
                We may update this statement to reflect website changes,
                accessibility improvements, evaluation results, or evolving
                standards and practices. The current version will be posted on
                this page with an updated review date.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Accessibility;